// clipboard.ts — clipboard + item-array-JSON helpers (template.js cleanup roadmap T2.2, 2026-06-14).
//
// Clipboard-copy family + the lazy item_arr JSON fetch/copy/view helpers, carved out of template.js.
// NOTE: the roadmap's T2.2 also listed delay / getUrlVars / redirectURL,
// but those are tiny generic utils interleaved with top-level side effects (isMobile detection, the
// mobile document.write style block, the YouTube-embed IIFE) and may be needed before template.js —
// deliberately LEFT IN PLACE. This bundle is just the clipboard/item-arr family.
//
// Public surface (window-attached at the bottom — all callers are runtime: PHP onclick across ~many
// forms/tables, the item-media bundle's management-action onclick (copyInputToClipboard), and the
// player-character-sheet bundle's share-button onclick (copyToClipboard)).
//
// Loaded AFTER template.js, so the globals these invoke at runtime — BASE_URL (core-helpers),
// ftDebugLog / displayWarning (template.js), toggleEl (dom-utils) — all resolve at call time.

function copyToClipboard(element: string | Element | null | undefined): void {
    const node = (typeof element === 'string') ? document.querySelector(element) : element;
    if (!node) return;
    // Only trust .value for real form fields. Plain elements like <li> also expose a `value`
    // property (an <li>'s list ordinal — 0 outside an <ol>), so the old `'value' in node` test
    // copied "0" instead of the element's text. Form fields → value, everything else → textContent.
    const isFormField = (
        node instanceof HTMLInputElement
        || node instanceof HTMLTextAreaElement
        || node instanceof HTMLSelectElement
    );
    const text = (isFormField && node.value != null && node.value !== '') ? node.value : (node.textContent ?? '');
    const temp = document.createElement('input');
    document.body.appendChild(temp);
    temp.value = text;
    temp.select();
    document.execCommand("copy");
    temp.remove();
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Example usage:
// const div = document.querySelector('#myDiv');
// copyHTMLtoClipboard(div);

async function copyHTMLtoClipboard(element: unknown): Promise<boolean> {
    // Check if element exists and is valid
    if (!element || !(element instanceof HTMLElement)) {
        ftDebugLog('clipboard', { warn: 'No valid element provided to copy from.' });
        return false;
    }

    try {
        // Get the raw text content from the element to preserve literal characters
        const text = element.textContent ?? '';

        // Use Clipboard API to copy the exact text content
        await navigator.clipboard.writeText(text);

        ftDebugLog('clipboard', { copied: true, textLen: text?.length });
        return true;
    } catch (err) {
        console.error('Failed to copy text content: ', err);
        return false;
    }
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Fetch an item array as pretty JSON from the lazy endpoint (avoids embedding the whole array inline).
class ItemArrJsonLoadError extends Error {}

async function fetchItemArrJson(id: unknown): Promise<string> {
    const owner = window as Window & { fetchAndCacheObject?: (id: string) => Promise<unknown> };
    if (!owner.fetchAndCacheObject || !owner.ftAdoptInventoryBase)
        throw new ItemArrJsonLoadError('Could not load the array JSON: reload the page and try again.');
    const row = await owner.fetchAndCacheObject(String(id ?? ''));
    if (!row) throw new ItemArrJsonLoadError('Could not load the array JSON.');
    await owner.ftAdoptInventoryBase(row);
    const result = row as unknown as Record<string, unknown>;
    if (result._entity_refusal || result._inventory_refusal)
        throw new ItemArrJsonLoadError('Could not load the complete array JSON.');
    return JSON.stringify(row, null, 4);
}

// Copy an item array as pretty JSON to the clipboard (paste straight into a .json file).
async function copyItemArrJson(id: unknown): Promise<boolean> {
    const jsonPromise = fetchItemArrJson(id);
    try {
        if (typeof ClipboardItem !== 'undefined') {
            const blobPromise = jsonPromise.then(json => new Blob([json], { type: 'text/plain' }));
            // A denial can happen before the browser consumes the promised Blob.
            void blobPromise.catch(() => {});
            // Start the write during the click, before a slow fetch spends user activation.
            await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blobPromise })]);
        } else {
            await navigator.clipboard.writeText(await jsonPromise);
        }
        ftDebugLog('clipboard', { copied: true, format: 'item-arr-json' });
        return true;
    } catch (err) {
        console.error('copyItemArrJson failed: ', err);
        let json: string;
        try {
            json = await jsonPromise;
        } catch (loadError) {
            if (typeof displayWarning === 'function') displayWarning(loadError instanceof ItemArrJsonLoadError
                ? loadError.message : 'Could not load the array JSON.');
            return false;
        }

        let target = document.getElementById('div_pc_item_arr');
        if (!target) {
            target = document.createElement('pre');
            target.id = 'div_pc_item_arr';
            target.className = 'text-left border rounded p-3 overflow-auto';
            document.body.appendChild(target);
        }
        target.textContent = json;
        target.dataset.loaded = '1';
        // viewItemArrJson toggles; always open the manual fallback, including an already open panel.
        target.style.display = 'none';
        await viewItemArrJson(id, target.id);
        target.scrollIntoView({ block: 'center' });
        if (typeof displayWarning === 'function') displayWarning('Could not copy array JSON to the clipboard. The JSON is open below for manual copying.');
        return false;
    }
}

// Lazily fetch the item array JSON into a <pre> target and toggle its visibility.
async function viewItemArrJson(id: unknown, targetId: string): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;
    if (!target.dataset.loaded) {
        try {
            target.textContent = await fetchItemArrJson(id);
            target.dataset.loaded = '1';
        } catch (err) {
            console.error('viewItemArrJson failed: ', err);
            target.textContent = 'Failed to load array JSON.';
        }
    }
    toggleEl(targetId);
}
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


function copyInputToClipboardWithItemName(
	input: string
): void {
  // Get the text field
  var copyText = document.getElementById(input) as HTMLInputElement;

  // Get the value from the additional input
  var additionalText = (document.getElementById('item_name') as HTMLInputElement | null)?.value || '';

  // Combine the text values
  var combinedText = copyText.value + ' ' + additionalText;

  // Create a temporary text area element
  var tempTextArea = document.createElement('textarea');
  tempTextArea.value = combinedText;
  document.body.appendChild(tempTextArea);

  // Select the text
  tempTextArea.select();

  // Copy the text inside the text area
  navigator.clipboard.writeText(tempTextArea.value);

  // Remove the temporary text area element
  document.body.removeChild(tempTextArea);
}



function copyInputToClipboard(promptInput: string, ...additionalInputs: string[]): void {
  // Get the main text field
  var copyText = document.getElementById(promptInput) as HTMLInputElement;

  // Start with the main input value
  var combinedText = copyText.value;

  // Add double line breaks after the main input
  combinedText += '\n\n';

  // Loop through additional inputs and add their values with double line breaks
  additionalInputs.forEach(inputId => {
    var additionalText = (document.getElementById(inputId) as HTMLInputElement | null)?.value || '';
    if (additionalText.trim() !== '') {
      combinedText += additionalText.trim() + '\n\n';
    }
  });

  // Trim the final combined text to remove any extra line breaks
  combinedText = combinedText.trim();

  // Create a temporary text area element
  var tempTextArea = document.createElement('textarea');
  tempTextArea.value = combinedText;
  document.body.appendChild(tempTextArea);

  // Select and copy the text
  tempTextArea.select();
  navigator.clipboard.writeText(tempTextArea.value);

  // Remove the temporary text area element
  document.body.removeChild(tempTextArea);
}



function copyInputsToClipboard(promptInput: string, ...additionalInputs: string[]): void {
	// Get the main text field
	var copyText = document.getElementById(promptInput) as HTMLInputElement;

	// Start with the main input value
	var combinedText = copyText.value;

	// Add double line breaks after the main input
	combinedText += '\n\n';

	// Loop through additional inputs and add their values with double line breaks
	additionalInputs.forEach(inputId => {
		var additionalText = (document.getElementById(inputId) as HTMLInputElement | null)?.value || '';
		if (additionalText.trim() !== '') {
			combinedText += additionalText.trim() + '\n\n';
		}
	});

	// Trim the final combined text to remove any extra line breaks
	combinedText = combinedText.trim();

	// Create a temporary text area element
	var tempTextArea = document.createElement('textarea');
	tempTextArea.value = combinedText;
	document.body.appendChild(tempTextArea);

	// Select and copy the text
	tempTextArea.select();
	navigator.clipboard.writeText(tempTextArea.value);

	// Remove the temporary text area element
	document.body.removeChild(tempTextArea);
}

// ===========================================================================================
// Window attachment — preserve the public surface the originals exposed in template.js. All are
// runtime-invoked (PHP onclick + item-media/PC-sheet bundle onclick strings). fetchItemArrJson is
// an internal helper but window-attached too for parity (cheap; avoids a latent miss).
// ===========================================================================================
declare global {
    interface Window {
        copyToClipboard: typeof copyToClipboard;
        copyHTMLtoClipboard: typeof copyHTMLtoClipboard;
        fetchItemArrJson: typeof fetchItemArrJson;
        copyItemArrJson: typeof copyItemArrJson;
        viewItemArrJson: typeof viewItemArrJson;
        copyInputToClipboardWithItemName: typeof copyInputToClipboardWithItemName;
        copyInputToClipboard: typeof copyInputToClipboard;
        copyInputsToClipboard: typeof copyInputsToClipboard;
    }
}

Object.assign(window, {
    copyToClipboard,
    copyHTMLtoClipboard,
    fetchItemArrJson,
    copyItemArrJson,
    viewItemArrJson,
    copyInputToClipboardWithItemName,
    copyInputToClipboard,
    copyInputsToClipboard,
});

export {};
